import {
  ChangeEvent,
  HTMLAttributes,
  KeyboardEvent,
  MouseEvent,
  SyntheticEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import ReactDOM from 'react-dom'
import { useHotkeys } from 'react-hotkeys-hook'
import { useNavigate, matchPath, useLocation, Link as RouterLink } from 'react-router-dom'
import MuiAutocomplete, { AutocompleteInputChangeReason } from '@mui/material/Autocomplete'
import MuiAvatar from '@mui/material/Avatar'
import CircularProgress from '@mui/material/CircularProgress'
import Link from '@mui/material/Link'
import ListItem from '@mui/material/ListItem'
import ListItemIcon from '@mui/material/ListItemIcon'
import ListItemText from '@mui/material/ListItemText'
import TextField from '@mui/material/TextField'
import { ClientInfoStatus, asClientInfoStatus } from '@common/interfaces/client'
import { ISearchResult, ISearchResultItem, SearchType } from '@common/interfaces/search'
import cn from 'classnames'
import { AutocompleteProps } from 'mui-rff'
import genericSs from '../../assets/styles/generic.module.scss'
import { ROUTES } from '../../constants/routes'
import {
  addLogoDevToken,
  debounceEventHandler,
  getInitials,
  highlightMatchesJSX,
} from '../../helpers/helpers'
import useAsyncSearch from '../../hooks/useAsyncSearch'
import Icon, { IconOrSvg, IconOrSvgGlyph } from '../Common/Icon'
import Text from '../Common/Text'
import { orderSearchResultSections, generateSearchResultItemLink } from './GlobalSearch.helpers'
import styles from './GlobalSearch.module.scss'
import { PropsFromRedux } from './index'

export const TYPE_ICONS: Partial<Record<SearchType, IconOrSvg>> = {
  [SearchType.Funding]: 'banknote',
  [SearchType.Client]: 'people',
  [SearchType.Prospect]: 'people',
  [SearchType.DueDiligence]: 'people',
  [SearchType.Collection]: 'collections',
  [SearchType.Fee]: 'receipt',
  [SearchType.Note]: 'file-text',
  [SearchType.OngoingReporting]: 'pie-chart',
  [SearchType.User]: 'user-management',
  [SearchType.Entity]: 'briefcase',
  [SearchType.Participant]: 'briefcase',
  [SearchType.Invoice]: 'receipt',
  [SearchType.Organization]: 'briefcase',
  [SearchType.Company]: 'people',
  [SearchType.Person]: 'user',
  [SearchType.Task]: 'check-circle',
  [SearchType.Sku]: 'inventory',
}

const SearchResultTypeIcon = ({ type, className }: { type: SearchType; className?: string }) => (
  <IconOrSvgGlyph icon={TYPE_ICONS[type] || 'search'} size="md" svgClassName={className} />
)

interface IProps
  extends Partial<AutocompleteProps<ISearchResultItem, false, false, true>>, PropsFromRedux {
  isMobile?: boolean
  onClose?: () => void
}
const replaceJSX = (str: string, find: string) => {
  return highlightMatchesJSX(str, find)
}

const GlobalSearchOption = ({
  props,
  option,
  inputValue,
}: {
  props: HTMLAttributes<HTMLLIElement>
  option: ISearchResultItem
  inputValue: string
}) => {
  const navigate = useNavigate()
  const renderTitle = useCallback((title: string) => replaceJSX(title, inputValue), [inputValue])

  const iconUrl = useMemo(() => {
    const item = option.item as { iconUrl?: string; avatar?: string }
    return [
      SearchType.Client,
      SearchType.Prospect,
      SearchType.DueDiligence,
      SearchType.Company,
    ].includes(option.type)
      ? addLogoDevToken(item.iconUrl)
      : [SearchType.User].includes(option.type)
        ? item.avatar
        : null
  }, [option])

  const handleClick = useCallback(
    (e: MouseEvent) => {
      if (option.type === SearchType.Invoice) {
        e.preventDefault()
        void navigate(`${ROUTES.GLOBAL_SEARCH}?invoiceId=${option.id}`)
      }
    },
    [option, navigate],
  )

  const content = (
    <ListItem {...props} key={option.id} className={styles.listItem} dense>
      <ListItemIcon
        className={cn(styles.listItemIcon, {
          [styles.listItemIconFunding]: option.type === SearchType.Funding,
        })}
      >
        {iconUrl ? (
          <MuiAvatar src={iconUrl} alt={option.title} className={styles.avatar}>
            {getInitials(option.title)}
          </MuiAvatar>
        ) : (
          <SearchResultTypeIcon type={option.type} className={styles.listItemIconSvg} />
        )}
      </ListItemIcon>
      <ListItemText
        classes={{
          root: styles.listItemText,
          primary: styles.listItemTextPrimary,
          secondary: styles.listItemTextSecondary,
        }}
        primary={renderTitle(option.title)}
        secondary={
          option.type === SearchType.Client ? (
            <Text variant="menuItem">
              Client
              {asClientInfoStatus(option.status) !== ClientInfoStatus.Current && (
                <span className={genericSs.grayCard}>{option.status}</span>
              )}
            </Text>
          ) : (
            renderTitle(option.status)
          )
        }
      />

      <Icon name="chevron-right" size="sm" className={styles.listItemNavigate} />
    </ListItem>
  )

  if (option.type === SearchType.Invoice) {
    return <div onClick={handleClick}>{content}</div>
  }

  return (
    <Link component={RouterLink} to={option.link}>
      {content}
    </Link>
  )
}

const GlobalSearchSeeAll = ({
  element,
  onClose,
}: {
  element: Element | null
  onClose?: () => void
}) => {
  if (!element) {
    return null
  }
  return ReactDOM.createPortal(
    <Link
      component={RouterLink}
      to={ROUTES.GLOBAL_SEARCH}
      className={styles.seeAllButton}
      data-cy="global-search-see-all"
      onClick={onClose}
    >
      See all results
    </Link>,
    element,
  )
}

const SEARCH_DEBOUNCE_MS = 200

const GlobalSearch = ({ isLoading, isBDO, search, isMobile, onClose }: IProps) => {
  const ref = useRef<HTMLDivElement>(null)
  const {
    results: asyncOptions,
    status,
    run,
    markSearching,
    reset,
  } = useAsyncSearch<ISearchResultItem>()

  const [inputValue, setInputValue] = useState('')
  const navigate = useNavigate()
  const location = useLocation()
  const inputref = useRef<HTMLInputElement>(null)
  const [listboxElement, setListboxElement] = useState<HTMLDivElement | null>(null)
  const [open, setOpen] = useState(false)
  const isGlobalSearchPage = useMemo(
    () => !!matchPath(location.pathname, ROUTES.GLOBAL_SEARCH),
    [location],
  )

  useHotkeys(
    'ctrl+shift+f',
    (event) => {
      event.preventDefault()
      inputref?.current?.focus()
    },
    {
      enabled: !isMobile,
    },
  )

  useEffect(() => {
    if (isMobile) {
      setOpen(true)
      requestAnimationFrame(() => {
        inputref?.current?.focus()
      })
    }
  }, [isMobile])

  const loadResults = useCallback(
    async (value: string) => {
      const res = (await search({
        search: value,
      })) as { data: ISearchResult[] }
      const results: ISearchResultItem[] = []
      orderSearchResultSections(res.data, value)
        .map(({ items }) => items)
        .flat()
        .forEach((data: ISearchResultItem) => {
          results.push({
            link: generateSearchResultItemLink(data, isBDO, location.pathname, location.search),
            title: data.title,
            id: data.id,
            type: data.type,
            status: data.status,
            item: data.item,
          })
        })
      return results
    },
    [search, isBDO, location.pathname, location.search],
  )

  const handleInputChange = useCallback(
    async (event: ChangeEvent<object>, value: string, reason: 'input' | 'reset' | 'clear') => {
      if (reason === 'input') {
        // A superseded reply is dropped, and a failed one leaves the last good
        // list alone rather than emptying the dropdown — see useAsyncSearch.
        await run(() => loadResults(value))
      }
    },
    [loadResults, run],
  )

  /**
   * How long after a keystroke the search is actually asked for.
   *
   * Was 500ms, which is half a second of waiting before the request even leaves
   * the browser, on top of however long the search itself takes. 200ms is inside
   * the range a typeahead is normally paced at and takes that third of a second
   * off every search.
   */
  const debounceHandleInputChange = useMemo(
    () => debounceEventHandler(handleInputChange, SEARCH_DEBOUNCE_MS),
    [handleInputChange],
  )

  const handleInputClear = useCallback(() => {
    setInputValue('')
    reset()
  }, [reset])

  /**
   * Marks the search as started on the keystroke, ahead of the debounce.
   *
   * The debounce window is a second of waiting from the reader's side, and
   * without this the dropdown spent all of it claiming it had an answer.
   */
  const onInputChange = useCallback(
    (event: SyntheticEvent, value: string, reason: AutocompleteInputChangeReason) => {
      if (reason === 'input') markSearching()
      // The whole clear button, not just its icon, resets the controlled input.
      if (reason === 'clear') handleInputClear()
      debounceHandleInputChange(event, value, reason)
    },
    [debounceHandleInputChange, markSearching, handleInputClear],
  )

  const onChangeInput = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => setInputValue(e.target.value),
    [],
  )

  const finishPick = useCallback(() => {
    handleInputClear()
    inputref.current?.blur()
    if (onClose) {
      onClose()
    }
  }, [handleInputClear, onClose])

  const handleSelectOption = useCallback(
    (event: SyntheticEvent, newValue: ISearchResultItem | string | null, reason: string) => {
      if (reason === 'selectOption') {
        finishPick()
      }
    },
    [finishPick],
  )

  useEffect(() => {
    if (!asyncOptions) {
      setListboxElement(null)
      return
    }
    if (!ref.current) return
    setListboxElement(() => ref.current?.querySelector('[role="presentation"] > div'))
  }, [asyncOptions])

  const handleFocus = useCallback(() => {
    setTimeout(() => {
      const element = ref.current?.querySelector<HTMLDivElement>('[role="presentation"] > div')
      if (element) {
        setListboxElement(element)
      }
    }, 0)
  }, [])

  const handleOpen = useCallback(() => {
    if (!isGlobalSearchPage) {
      setOpen(true)
    }
  }, [isGlobalSearchPage])

  const handleClose = useCallback(
    (event: SyntheticEvent, reason: string) => {
      if (reason === 'blur') {
        setTimeout(() => {
          setOpen(false)
          if (isMobile && onClose) {
            onClose()
          }
        }, 200)
      } else {
        setOpen(false)
      }
    },
    [isMobile, onClose],
  )

  const handleKeyPress = useCallback(
    (event: KeyboardEvent<HTMLInputElement>) => {
      if (event.key === 'Enter') {
        if (!isGlobalSearchPage) {
          if (asyncOptions.length) {
            if (listboxElement) {
              const focusedItemLink = listboxElement
                .querySelector('[class*="Mui-focused"]')
                ?.closest('a')
                ?.getAttribute('href')
              if (focusedItemLink) {
                void navigate(focusedItemLink)
                finishPick()
                return
              }
            }
            // Nothing highlighted — jump to the top result rather than the full
            // search-results page (falls back to the results page if it has no link).
            const topResultLink = asyncOptions[0]?.link
            void navigate(topResultLink || ROUTES.GLOBAL_SEARCH)
            finishPick()
          }
        }
      }
    },
    [navigate, isGlobalSearchPage, asyncOptions, listboxElement, finishPick],
  )

  return (
    <div ref={ref} className={cn(styles.container, { [styles.containerMobile]: isMobile })}>
      <MuiAutocomplete
        ListboxProps={
          {
            'data-cy': 'global-search-listbox',
          } as HTMLAttributes<HTMLElement>
        }
        renderGroup={({ key, group, children }) => (
          <li key={key} data-cy={`autosuggest-group`}>
            <div data-cy="autosuggest-group-label">{group}</div>
            <ul className={styles.listbox}>{children}</ul>
          </li>
        )}
        open={open}
        key={1}
        disablePortal
        options={asyncOptions}
        getOptionLabel={(option: ISearchResultItem | string) =>
          (typeof option === 'string' ? option : option?.title) || ''
        }
        className={cn()}
        classes={{
          input: styles.input,
          listbox: styles.listbox,
          paper: styles.paper,
          popper: styles.popper,
          focused: styles.focused,
          groupLabel: styles.groupLabel,
          clearIndicator: styles.clearIcon,
          endAdornment: styles.endAdornment,
        }}
        clearIcon={<Icon name="x" size="sm" />}
        renderOption={(props: HTMLAttributes<HTMLLIElement>, option: ISearchResultItem) => (
          <GlobalSearchOption
            key={`${option.type}-${option.id}`}
            props={{
              ...props,
              ['data-cy' as string]: 'autosuggest-option',
            }}
            option={option}
            inputValue={inputValue}
          />
        )}
        filterOptions={(options) => options}
        renderInput={(params) => {
          const { InputProps, ...otherParams } = params
          return (
            <TextField
              data-cy="global-search-input"
              {...otherParams}
              variant="standard"
              onChange={onChangeInput}
              placeholder={isMobile ? 'Search...' : 'Search [Ctl+Shift+F]'}
              slotProps={{
                input: {
                  ...InputProps,
                  className: cn(styles.inputField, styles.inputFieldMediumHeight),
                  classes: {
                    focused: styles.focusedInput,
                  },
                  disableUnderline: true,
                  startAdornment:
                    !isGlobalSearchPage && (isLoading || status === 'searching') ? (
                      <CircularProgress size={16} className={styles.inputLoaderIcon} />
                    ) : (
                      <span className={styles.inputIcon}>
                        <Icon name="search" size="md" />
                      </span>
                    ),

                  inputRef: inputref,
                  onKeyDown: handleKeyPress,
                },
              }}
            />
          )
        }}
        freeSolo
        autoHighlight
        inputValue={inputValue}
        groupBy={(option: ISearchResultItem) => option.type}
        onInputChange={onInputChange}
        openOnFocus
        onFocus={handleFocus}
        /* No `noOptionsText`: MUI does not render it in freeSolo mode, so an empty
           dropdown simply shows nothing. The spinner in the input is what reports
           that a search is running. */
        onChange={handleSelectOption}
        onOpen={handleOpen}
        onClose={handleClose}
      />

      {!isBDO && (
        <GlobalSearchSeeAll element={asyncOptions.length > 0 && listboxElement} onClose={onClose} />
      )}
    </div>
  )
}

export default GlobalSearch
